<script lang="ts" setup>
import dlaWokalistowImg from '@/assets/dla-wokalistow.webp'
import dlaLiderowImg from '@/assets/dla-liderow.webp'
import dlaMuzykowImg from '@/assets/dla-muzykow.webp'
import dlaZespolowImg from '@/assets/dla-zespolow.webp'
import { getAppUrl } from '@/lib/helpers'

const forWho = ref<'vocalists' | 'leaders' | 'instrumentalists' | 'bands'>('vocalists')
</script>

<template>
  <v-container id="dla-kogo">
    <div class="d-flex flex-column ga-5">
      <div class="mb-1 text-center">
        <v-chip
          color="gray-300"
          variant="outlined"
          text="Dla kogo?"
        />
      </div>
      <div class="text-h3 text-center">
        Dla wszystkich chwalców
      </div>
      <div
        class="text-subtitle-1 text-gray-500 font-weight-regular mx-auto text-center"
        :style="{ maxWidth: '550px'}"
      >
        CSM/online powstało z myślą o każdym członku zespołu uwielbienia, niezależnie od doświadczenia czy roli.
      </div>

      <div class="d-flex align-center justify-center my-7">
        <div
          class="px-2 rounded-lg bg-gray-50 border-gray-100 border-sm border-opacity-100 w-100"
          :style="{ maxWidth: '1100px' }"
        >
          <v-tabs
            v-model="forWho"
            hide-slider
            content-class="align-center"
            selected-class="bg-white elevation-1"
            :style="{ height: '60px' }"
            grow
          >
            <v-tab
              v-for="(value, key) in {
                vocalists: 'Dla wokalistów',
                instrumentalists: 'Dla instrumentalistów',
                bands: 'Dla zespołów',
                leaders: 'Dla liderów i pastorów',
              }"
              :key="key"
              variant="text"
              :value="key"
              :text="value"
              rounded="lg"
              min-height="44"
              color="gray-700"
              base-color="gray-500"
            />
          </v-tabs>
        </div>
      </div>

      <v-tabs-window v-model="forWho">
        <v-tabs-window-item value="vocalists">
          <div class="d-flex flex-row ga-12">
            <v-card
              class="pa-10 d-flex flex-column ga-8 ga-md-20"
              color="primary-900"
              rounded="xl"
              :max-width="$vuetify.display.mdAndUp ? 500 : undefined"
            >
              <div class="text-md-h3 text-h4 font-weight-regular">
                Śpiewaj pewnie<br>
                w harmonii
              </div>

              <div class="d-flex flex-column ga-4">
                <div class="text-h6 font-weight-medium">
                  Nic nie daje takiej radości jak<br>
                  wspólny śpiew na głosy.
                </div>
                <div class="text-body-1 opacity-70">
                  Na naszej platformie znajdziesz gotowe opracowania z podziałem na głosy w formie nut i nagrań wideo oraz materiały, które pomogą Ci rozwijać technikę wokalną.
                  Niezależnie od tego, czy śpiewasz głos prowadzący, sopran, alt czy tenor, zawsze będziesz dokładnie wiedział, co i jak śpiewać, aby idealnie współgrać z zespołem.
                </div>
              </div>

              <div>
                <v-btn
                  variant="outlined"
                  rounded="xll"
                  prepend-icon="mdi-arrow-right-circle"
                  text="Zobacz więcej"
                  :href="getAppUrl()"
                />
              </div>
            </v-card>
            <v-img
              v-if="$vuetify.display.mdAndUp"
              color="gray-300"
              :src="dlaWokalistowImg"
              rounded="xl"
              cover
            />
          </div>
        </v-tabs-window-item>
        <v-tabs-window-item value="instrumentalists">
          <div class="d-flex flex-row ga-12">
            <v-card
              class="pa-10 d-flex flex-column ga-8 ga-md-20"
              color="#4F2305"
              rounded="xl"
              :max-width="$vuetify.display.mdAndUp ? 500 : undefined"
            >
              <div class="text-md-h3 text-h4 font-weight-regular">
                Ćwicz w swoim tempie
              </div>

              <div class="d-flex flex-column ga-4">
                <div class="text-h6 font-weight-medium">
                  Nie musisz spisywać partii ze słuchu ani przekopywać YouTube’a w poszukiwaniu odpowiedniego tutorialu.
                </div>
                <div class="text-body-1 opacity-70">
                  Nasze lekcje wideo, krok po kroku pokazują jak zagrać każdą sekcję utworu i dają pewność, że zagrasz dokładnie to, co trzeba, we właściwym momencie.
                  Prowadzący dzielą się praktycznymi wskazówkami dotyczącymi techniki i brzmienia, więc uczysz się nie tylko konkretnych piosenek, ale rozwijasz też swój muzyczny warsztat.
                  Każdy utwór możesz ćwiczyć w swoim tempie – zwalniając nagrania i powtarzając trudniejsze fragmenty, aż poczujesz się pewnie.
                  Dzięki temu przygotowania do uwielbienia stają się prostsze i bardziej satysfakcjonujące.
                </div>
              </div>

              <div>
                <v-btn
                  variant="outlined"
                  rounded="xll"
                  prepend-icon="mdi-arrow-right-circle"
                  text="Zobacz więcej"
                  :href="getAppUrl()"
                />
              </div>
            </v-card>
            <v-img
              v-if="$vuetify.display.mdAndUp"
              color="gray-300"
              rounded="xl"
              :src="dlaMuzykowImg"
              cover
            />
          </div>
        </v-tabs-window-item>
        <v-tabs-window-item value="bands">
          <div class="d-flex flex-row ga-12">
            <v-card
              class="pa-10 d-flex flex-column ga-8 ga-md-20"
              color="#101828"
              rounded="xl"
              :max-width="$vuetify.display.mdAndUp ? 500 : undefined"
            >
              <div class="text-md-h3 text-h4 font-weight-regular">
                Koniec długich<br class="d-none d-md-block">
                i nieefektywnych prób
              </div>

              <div class="d-flex flex-column ga-4 flex-grow-1">
                <div class="text-h6 font-weight-medium">
                  Z CSM/online cały zespół – lider, instrumentaliści i wokaliści – uczy się z tych samych opracowań i tutoriali.
                </div>
                <div class="text-body-1 opacity-70">
                  Każdy może dokładnie wyćwiczyć swoją partię w domu i jeszcze lepiej przygotować się do próby.
                  Dzięki temu wasz zespół zabrzmi spójnie, odnajdziecie przyjemność ze wspólnego muzykowania
                  i będziecie mogli się skupić na tym, co w uwielbieniu najważniejsze.
                </div>
              </div>

              <div>
                <v-btn
                  variant="outlined"
                  rounded="xll"
                  prepend-icon="mdi-arrow-right-circle"
                  text="Zobacz więcej"
                  :href="getAppUrl()"
                />
              </div>
            </v-card>
            <v-img
              v-if="$vuetify.display.mdAndUp"
              color="gray-300"
              rounded="xl"
              :src="dlaZespolowImg"
              cover
            />
          </div>
        </v-tabs-window-item>
        <v-tabs-window-item value="leaders">
          <div class="d-flex flex-row ga-12">
            <v-card
              class="pa-10 d-flex flex-column ga-8 ga-md-20"
              color="#054F31"
              rounded="xl"
              :max-width="$vuetify.display.mdAndUp ? 500 : undefined"
            >
              <div class="text-md-h3 text-h4 font-weight-regular">
                Rozwijaj służbę<br>
                uwielbienia
              </div>

              <div class="d-flex flex-column ga-4">
                <div class="text-h6 font-weight-medium">
                  CSM/online jest narzędziem, które wspiera budowanie i prowadzenie służby uwielbienia w lokalnych Kościołach i wspólnotach.
                </div>
                <div class="text-body-1 opacity-70">
                  Dostęp do stale rosnącej bazy opracowań pieśni uwielbienia znaczenie ułatwia wprowadzanie nowych utworów do repertuaru służby uwielbienia.
                  W Strefie Inspiracji znajdziesz liczne wykłady i warsztaty, które pomagają rozwijać umiejętności liderów, wspierać zespół w codziennej pracy i inspirować do prowadzenia wspólnoty w modlitwie uwielbienia.
                </div>
              </div>

              <div>
                <v-btn
                  variant="outlined"
                  rounded="xll"
                  prepend-icon="mdi-arrow-right-circle"
                  text="Zobacz więcej"
                  :href="getAppUrl()"
                />
              </div>
            </v-card>
            <v-img
              v-if="$vuetify.display.mdAndUp"
              color="gray-300"
              rounded="xl"
              :src="dlaLiderowImg"
              cover
            />
          </div>
        </v-tabs-window-item>
      </v-tabs-window>
    </div>
  </v-container>
</template>
